<template>
  <v-card v-if="!isEmpty(productOptions)" class="route-products" :color="zone + ' lighten-3'">
    <v-card-subtitle>{{label}} ({{ $t('n_of_m', { n: this.routeProducts.length, m: this.productOptions.length}) }})</v-card-subtitle>
    <v-card-text>
      <v-card v-for="(routeProduct, index) in routeProducts" :key="routeProduct.id" elevation="1" class="mb-3 pt-1">
        <v-card-text>
          <div class="d-flex mb-4">
            <div class="flex-grow-1">
              <v-select
                dense
                :disabled="productOptions.length === 1 && selectedProductIds.length === 1"
                v-model="routeProduct.product"
                item-value="id"
                item-text="name"
                return-object
                :items="productOptions"
                :error-messages="routeProductErrors[index].product"
                :label="$tc('product')">
                <template #selection="{ item, disabled }">
                <span v-if="disabled" class="blue-grey--text">
                  {{ item.name }}
                </span>
                  <span v-else>
                  {{ item.name }}
                </span>
                </template>
              </v-select>
              <v-text-field
                v-model="routeProduct.start_temp"
                :append-icon="routeProduct.temperatureIcon()"
                :disabled="readOnlyStartTemps"
                :error-messages="routeProductErrors[index].start_temp"
                :placeholder="$t('starting_temperature')"/>
            </div>
            <div class="flex-grow-0 align-self-center ml-4">
              <vf-tooltip :text="$t('remove_this_product')">
                <v-btn
                  fab x-small dense elevation="0"
                  tabindex="-1"
                  color="danger"
                  dark
                  @click="removeRouteProduct(index)">
                  <v-icon>mdi-minus</v-icon>
                </v-btn>
              </vf-tooltip>
            </div>
          </div>
        </v-card-text>
      </v-card>
      <v-btn
        tabindex="-1"
        small
        @click="addEntry"
        color="primary"
        :disabled="this.routeProducts.length >= this.productOptions.length">
        <v-icon>mdi-plus</v-icon> {{ $t('add_product')}}
      </v-btn>
    </v-card-text>
  </v-card>
  <v-card v-else :color="zone + ' lighten-3'" height="280" class="p-3">
    <v-skeleton-loader type="image" height="180"/>
    <v-skeleton-loader type="button" class="mt-2"/>
  </v-card>
</template>

<script>
import TemperatureConverterUtil from '@/util/TemperatureConverterUtil'
import VfTooltip from '@/components/shared/VfTooltip'
import OldValidationMixin from '@/components/mixins/OldValidationMixin'
import { required } from 'vuelidate/lib/validators'
import { filter, cloneDeep, isEmpty, each, map } from 'lodash'

export default {
  name: 'RouteProductSelection',
  components: {
    VfTooltip
  },
  mixins: [
    OldValidationMixin
  ],
  props: {
    zone: String,
    label: String,
    readOnlyStartTemps: {
      type: Boolean,
      default: false
    },
    value: {
      type: Array,
      default: () => []
    },
    state: {
      type: Boolean,
      default: null
    },
    populateWithDefaults: {
      type: Boolean,
      default: false
    },
    routeCustomer: Object
  },
  validations: {
    routeProducts: {
      $each: {
        product: {
          required
        },
        start_temp: {
          required,
          validateStartTemp: (value, vm) => {
            if (value != null && value !== '' && vm.product) {
              return value >= vm.product.min_start_temp && value <= vm.product.max_start_temp
            }
            return false
          }
        }
      }
    }
  },
  data () {
    return {
      loading: false,
      routeProducts: cloneDeep(this.value),
      productOptions: []
    }
  },
  computed: {
    /**
     * Keep track of which products are currently selected across all inputs
     */
    selectedProductIds () {
      return filter(this.routeProducts, 'product').map(rp => rp.product.id)
    },
    routeProductErrors () {
      return map(this.$v.routeProducts.$each.$iter, validation => {
        const errors = {}
        if (validation.$error === true) {
          if (validation.product.required === false) {
            errors.product = this.$t('product_selection_required')
          }
          if (validation.start_temp.required === false) {
            errors.start_temp = 'Please enter a Start Temperature.'
          } else if (validation.start_temp.validateStartTemp === false) {
            errors.start_temp = this.$t('temp_value_error', {
              min_temp: this.formatTempHelper(validation.$model.product.min_start_temp),
              max_temp: this.formatTempHelper(validation.$model.product.max_start_temp),
              temperatureFormat: validation.$model.temperature_scale
            })
          }
        }
        return errors
      })
    }
  },
  watch: {
    /**
     * When selectedProductIds is updated, flag all selected products as disabled.
     */
    selectedProductIds () {
      each(this.productOptions, product => {
        product.disabled = this.selectedProductIds.includes(product.id)
      })
    },
    routeProducts (newValue) {
      this.$emit('input', newValue)
    }
  },
  methods: {
    isEmpty: isEmpty,
    /**
     * Initialize the component
     */
    initialize () {
      this.routeProducts = cloneDeep(this.value)
      this.fetchProductOptions().then(() => {
        this.populateDefaults()
      })
    },
    /**
     * Reset component back to initial state
     */
    reset () {
      this.$v.$reset()
      this.initialize()
    },
    /**
     * Add a new route product entry to the form
     */
    addEntry () {
      // Don't add more route products than there are products total.
      if (this.routeProducts.length < this.productOptions.length) {
        const newRecord = this.$orm('RouteProduct').createRecord({
          temperature_scale: this.routeCustomer.tempFormat()
        })
        // If there's only one route product available, add it automatically.
        if (this.productOptions.length === 1) {
          newRecord.product = this.productOptions[0]
        }
        this.routeProducts.push(newRecord)
      }
    },
    /**
     * Remove the route product at the specified index.
     * @param index
     */
    removeRouteProduct (index) {
      this.$delete(this.routeProducts, index)
    },
    /**
     * Validate the form
     */
    validate () {
      this.$v.$touch()
      return !this.$v.$invalid
    },
    /**
     * Small convenience function to invoke TemperatureConverterUtil on templates
     * @param temp
     */
    formatTempHelper (temp) {
      return TemperatureConverterUtil.formatTemperature(temp)
    },
    /**
     * Fetch the available products for this customer and zone.
     */
    async fetchProductOptions () {
      try {
        this.loading = true
        const response = await this.$orm('Product').findAll({
          customer_id: this.routeCustomer.id,
          zone: this.zone
        })
        this.productOptions = response.getItems()
        if (isEmpty(this.productOptions)) {
          // No route plans available, emit an event
          this.$emit('no-products')
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loading = false
      }
    },
    /**
     * Populate the default products, if populateWithDefaults is enabled.
     * Default products are denoted by the "add_by_default" field on the products.
     */
    populateDefaults () {
      if (this.populateWithDefaults) {
        const defaultProducts = filter(this.productOptions, (product) => product.add_by_default === 1)
        each(defaultProducts, product => {
          const newProduct = this.$orm('RouteProduct').createRecord({
            product: product,
            temperature_scale: this.routeCustomer.tempFormat()
          })
          this.routeProducts.push(newProduct)
        })
      }
    }
  },
  mounted () {
    this.initialize()
  }
}

</script>

<style lang="scss" scoped>
</style>
